import { useMemo } from "react"
import { cn } from "@/lib/utils"
import { ParameterItem } from "./parameter-item"
import { buildSegments } from "./sentence-segments"
import type { NodeParameter } from "./parameter-utils"

type SentenceRendererProps = {
	sentence: string
	parameters: NodeParameter[]
	connectionInfo: {
		inputs: Set<string>
		resolvedInputs: Set<string>
		sourceLabels: Map<string, string>
		values: Record<string, unknown>
	}
	onParameterChange: (paramName: string, value: unknown) => void
	showHandles?: boolean
	className?: string
	variant?: "canvas" | "sidebar"
	isNodeSelected?: boolean
}

export const SentenceRenderer = ({
	sentence,
	parameters,
	connectionInfo,
	onParameterChange,
	showHandles = true,
	className,
	variant = "canvas",
	isNodeSelected = false,
}: SentenceRendererProps) => {
	const segments = useMemo(
		() => buildSegments(sentence, parameters),
		[sentence, parameters],
	)
	const parameterByName = useMemo(
		() => new Map(parameters.map((param) => [param.name, param])),
		[parameters],
	)
	const renderedParameterNames = useMemo(
		() =>
			new Set(
				segments
					.filter((segment) => segment.type === "parameter")
					.map((segment) => segment.name),
			),
		[segments],
	)
	const fallbackParameters = useMemo(
		() =>
			parameters.filter(
				(parameter) => !renderedParameterNames.has(parameter.name),
			),
		[parameters, renderedParameterNames],
	)
	return (
		<div
			className={cn(
				variant === "sidebar"
					? "rounded-lg bg-muted/20 p-2 dark:bg-muted/30"
					: "px-1 py-0.5",
				className,
			)}
		>
			<div
				className={cn(
					"flex flex-wrap items-center",
					variant === "sidebar"
						? "gap-x-0.5 gap-y-0.5 text-sm leading-[1.5] text-foreground/70"
						: cn(
								"gap-x-[5px] gap-y-[5px] text-[14px] leading-[normal] font-normal",
								isNodeSelected ? "text-white" : "text-[var(--node-text)]",
							),
				)}
			>
				{segments.map((segment) => {
					if (segment.type === "text") {
						if (!segment.content.trim()) return null
						return (
							<span key={segment.key} className="inline whitespace-pre">
								{segment.content}
							</span>
						)
					}
					const parameter = parameterByName.get(segment.name)
					if (!parameter) return null
					return (
						<ParameterItem
							key={segment.key}
							parameter={parameter}
							isConnected={connectionInfo.inputs.has(parameter.name)}
							hasResolvedValue={connectionInfo.resolvedInputs.has(
								parameter.name,
							)}
							resolvedValue={connectionInfo.values[parameter.name]}
							sourceLabel={
								connectionInfo.sourceLabels.get(parameter.name) ?? ""
							}
							showLabel={segment.showLabel}
							showHandle={showHandles}
							onChange={(value) => onParameterChange(parameter.name, value)}
							variant={variant}
						/>
					)
				})}
				{fallbackParameters.map((parameter) => (
					<ParameterItem
						key={`fallback:${parameter.name}`}
						parameter={parameter}
						isConnected={connectionInfo.inputs.has(parameter.name)}
						hasResolvedValue={connectionInfo.resolvedInputs.has(parameter.name)}
						resolvedValue={connectionInfo.values[parameter.name]}
						sourceLabel={connectionInfo.sourceLabels.get(parameter.name) ?? ""}
						showLabel={true}
						showHandle={showHandles}
						onChange={(value) => onParameterChange(parameter.name, value)}
						variant={variant}
					/>
				))}
			</div>
		</div>
	)
}
